/*  OfficeVault — /office/vault
 *
 *  The Production House's Virtual Office floor at the ORG level.
 *  Ships with two standing cubicles today:
 *    · Legal    — retained counsel + law firms
 *    · Accounts — retained accountants / CAs
 *
 *  Same invite mechanic as project cubicles — invite by name / phone
 *  / firm-name, then flip to Retained once negotiated. Monthly retainer
 *  amount is visible only to finance / owner / admin (RBAC).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Building2, Gavel, Wallet, Plus, X, Award, PowerOff, Trash2,
  Users, Lock, Eye, EyeOff, Send, ChevronLeft,
} from "lucide-react";

const ICONS = { Gavel, Wallet };
const CUBICLE_ACCENTS = { legal: "#818CF8", accounts: "#22D3EE" };


export default function OfficeVault() {
  const [catalog, setCatalog] = useState(null);
  const [active,  setActive]  = useState("legal");
  const [rows,    setRows]    = useState([]);
  const [invOpen, setInvOpen] = useState(false);
  const [retainFor, setRetainFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: c } = await api.get("/orgs/mine/office-cubicles");
      setCatalog(c);
      const { data: r } = await api.get(
        `/orgs/mine/office-cubicles/${active}/invites`);
      setRows(r.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail)
        || "You need Owner / Admin / Finance / Legal role to open this vault");
    }
  }, [active]);
  useEffect(() => { load(); }, [load]);

  if (!catalog) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white/55 flex items-center justify-center text-xs uppercase tracking-[0.28em]">
        Loading office vault…
      </div>
    );
  }
  const accent = CUBICLE_ACCENTS[active] || "#94A3B8";

  return (
    <div className="min-h-screen bg-neutral-950 text-white/90"
         data-testid="office-vault-page">
      <div className="border-b border-white/10 bg-neutral-950/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/dashboard"
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-white/55 hover:text-white"
                data-testid="office-vault-back">
            <ChevronLeft size={12}/> Dashboard
          </Link>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
            <Building2 size={12} className="inline mr-1"/> Virtual Office · Retainers
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
          The Studio Floor
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl uppercase tracking-tight">
          On Retainer
        </h1>
        <p className="mt-3 text-sm text-white/60 max-w-2xl">
          Counsel and accountants stay with you between projects. Invite
          a firm or an individual, negotiate the terms, and flip to
          Retained — the monthly fee stays inside the finance ring.
        </p>

        {/* Cubicle tiles */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
             data-testid="office-vault-tiles">
          {catalog.cubicles.map((c) => {
            const Icon = ICONS[c.icon] || Building2;
            const stats = catalog.counts?.[c.key] || {};
            const isActive = c.key === active;
            return (
              <button key={c.key}
                      onClick={() => setActive(c.key)}
                      className={"cs-card rounded-2xl p-5 text-left transition "
                        + (isActive
                           ? "border border-white/25 bg-white/[0.04]"
                           : "border border-white/10 hover:border-white/20")}
                      data-testid={`office-vault-tile-${c.key}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                       style={{ background: `${CUBICLE_ACCENTS[c.key]}22`,
                                color: CUBICLE_ACCENTS[c.key] }}>
                    <Icon size={18}/>
                  </div>
                  <div className="flex-1">
                    <div className="font-display text-xl uppercase tracking-tight">
                      {c.label}
                    </div>
                    <div className="text-[11px] text-white/55 mt-0.5">
                      {c.blurb}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-4 text-[10px] tracking-[0.22em] uppercase text-white/50">
                  <span data-testid={`office-vault-count-total-${c.key}`}>
                    {stats.total || 0} contacts
                  </span>
                  <span className="text-emerald-300"
                        data-testid={`office-vault-count-retained-${c.key}`}>
                    {stats.retained || 0} on retainer
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active cubicle body */}
        <div className="mt-10">
          <div className="flex items-baseline gap-3 justify-between flex-wrap">
            <div className="flex items-baseline gap-3">
              <div className="text-[10px] tracking-[0.28em] uppercase"
                   style={{ color: accent }}>
                {catalog.cubicles.find((c) => c.key === active)?.label || active} · Cubicle
              </div>
              <div className="text-[10px] text-white/50">
                {rows.length} total
              </div>
            </div>
            <Button onClick={() => setInvOpen(true)}
                    className="btn-themed h-9 px-4 rounded-full uppercase tracking-[0.22em] text-[10px]"
                    data-testid={`office-vault-invite-btn-${active}`}>
              <Plus size={12}/> Invite {active === "legal" ? "counsel / firm" : "accountant / CA"}
            </Button>
          </div>

          <div className="mt-5 space-y-3">
            {rows.length === 0 && (
              <div className="cs-card rounded-2xl border border-white/10 p-8 text-center"
                   data-testid={`office-vault-empty-${active}`}>
                <div className="text-sm text-white/55">
                  No contacts yet. Invite {active === "legal"
                    ? "a lawyer or a law firm — great time to bring one on before your next project."
                    : "an accountant or a CA firm — audits and filings run smoother with a retainer."}
                </div>
              </div>
            )}
            {rows.map((r) => (
              <RetainerRow key={r.id} row={r}
                           cubicle={active} accent={accent}
                           onRetain={() => setRetainFor(r)}
                           onEnd={async () => {
                             if (!window.confirm("End this retainer? Contact stays in the list marked ended.")) return;
                             try {
                               await api.post(
                                 `/orgs/mine/office-cubicles/${active}/invites/${r.id}/end`);
                               toast.success("Retainer ended");
                               load();
                             } catch (e) {
                               toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
                             }
                           }}
                           onDelete={async () => {
                             if (!window.confirm("Remove this contact entirely?")) return;
                             try {
                               await api.delete(
                                 `/orgs/mine/office-cubicles/${active}/invites/${r.id}`);
                               toast.success("Removed");
                               load();
                             } catch (e) {
                               toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
                             }
                           }}/>
            ))}
          </div>
        </div>
      </div>

      {invOpen && (
        <InviteModal
          cubicle={active}
          accent={accent}
          onClose={() => setInvOpen(false)}
          onCreated={() => { setInvOpen(false); load(); }}
        />
      )}
      {retainFor && (
        <RetainModal
          row={retainFor}
          cubicle={active}
          accent={accent}
          onClose={() => setRetainFor(null)}
          onDone={() => { setRetainFor(null); load(); }}
        />
      )}
    </div>
  );
}


function RetainerRow({ row, cubicle, accent, onRetain, onEnd, onDelete }) {
  const [reveal, setReveal] = useState(false);
  const isRetained = row.status === "retained";
  const isEnded    = row.status === "ended";
  const pill = isRetained ? "bg-emerald-500/15 text-emerald-300"
             : isEnded    ? "bg-white/6 text-white/50"
             :               "bg-amber-500/15 text-amber-300";
  return (
    <div className="cs-card rounded-2xl border border-white/10 p-4 flex items-center gap-4 flex-wrap"
         data-testid={`office-vault-row-${row.id}`}>
      <div className="w-11 h-11 rounded-lg bg-white/6 flex items-center justify-center">
        {row.contact_type === "company"
          ? <Building2 size={18} className="text-white/60"/>
          : <Users size={16} className="text-white/60"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg uppercase tracking-tight leading-tight">
          {row.contact_type === "company"
            ? row.company_name
            : row.name}
        </div>
        <div className="mt-0.5 text-[11px] text-white/55 flex items-center gap-2 flex-wrap">
          <span>{row.role_label}</span>
          {row.contact_type === "company" && row.name && (
            <span>· via {row.name}</span>
          )}
          {row.phone && <span>· {row.phone}</span>}
          {row.email && <span>· {row.email}</span>}
        </div>
        {isRetained && (
          <div className="mt-1 text-[10px]">
            {row.monthly_retainer != null ? (
              <button type="button"
                      onClick={() => setReveal((v) => !v)}
                      className="inline-flex items-center gap-1 tabular-nums"
                      style={{ color: accent }}
                      data-testid={`office-vault-amount-${row.id}`}>
                <Wallet size={10}/>
                {reveal
                  ? new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: row.currency || "INR",
                      maximumFractionDigits: 0,
                    }).format(row.monthly_retainer / 100) + "/mo"
                  : "•••• /mo"}
                {reveal ? <EyeOff size={10}/> : <Eye size={10}/>}
              </button>
            ) : (
              <div className="text-white/40 inline-flex items-center gap-1"
                   data-testid={`office-vault-amount-locked-${row.id}`}>
                <Lock size={10}/> Amount visible to accounts
              </div>
            )}
          </div>
        )}
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.22em] uppercase ${pill}`}
            data-testid={`office-vault-status-${row.id}`}>
        {row.status}
      </span>
      {!isRetained && !isEnded && (
        <Button size="sm" onClick={onRetain}
                className="rounded-full h-8 px-4 uppercase tracking-[0.2em] text-[10px]"
                style={{ background: accent, color: "#0a0812" }}
                data-testid={`office-vault-retain-btn-${row.id}`}>
          <Award size={11}/> Retain
        </Button>
      )}
      {isRetained && (
        <Button size="sm" variant="ghost" onClick={onEnd}
                className="rounded-full h-8 px-3 text-red-300 hover:text-red-100 uppercase tracking-[0.2em] text-[10px]"
                data-testid={`office-vault-end-btn-${row.id}`}>
          <PowerOff size={11}/> End
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={onDelete}
              className="rounded-full h-8 w-8 p-0 text-white/40 hover:text-white/70"
              data-testid={`office-vault-delete-btn-${row.id}`}>
        <Trash2 size={12}/>
      </Button>
    </div>
  );
}


function InviteModal({ cubicle, accent, onClose, onCreated }) {
  const [contactType, setContactType] = useState("individual");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [roleLabel, setRoleLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (contactType === "company" && !companyName.trim()) {
      toast.error("Firm / company name is required"); return;
    }
    if (contactType === "individual" && !name.trim()) {
      toast.error("Contact name is required"); return;
    }
    setBusy(true);
    try {
      await api.post(`/orgs/mine/office-cubicles/${cubicle}/invites`, {
        contact_type: contactType,
        name: name.trim(),
        company_name: companyName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        role_label: roleLabel.trim(),
      });
      toast.success(`${contactType === "company" ? "Firm" : "Contact"} added to ${cubicle} cubicle`);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="office-vault-invite-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-neutral-950 border border-white/15 rounded-2xl p-6 max-w-md w-full">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{color: accent}}>
          Invite · {cubicle === "legal" ? "Legal" : "Accounts"}
        </div>
        <div className="font-display text-2xl mt-2 uppercase tracking-tight">
          Bring them on retainer
        </div>

        <div className="mt-5 flex items-center gap-2">
          <span className="text-[9px] tracking-[0.22em] uppercase text-white/50">
            Inviting a
          </span>
          {["individual", "company"].map((t) => (
            <button key={t} type="button"
                    onClick={() => setContactType(t)}
                    className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] transition ${
                      contactType === t
                        ? "bg-white text-neutral-900"
                        : "border border-white/15 text-white/70 hover:text-white"
                    }`}
                    data-testid={`office-vault-contact-type-${t}`}>
              {t === "individual"
                ? (cubicle === "legal" ? "Individual Counsel" : "Individual CA")
                : (cubicle === "legal" ? "Law Firm" : "CA Firm")}
            </button>
          ))}
        </div>

        {contactType === "company" && (
          <div className="mt-3">
            <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
              {cubicle === "legal" ? "Law firm *" : "CA firm *"}
            </label>
            <Input value={companyName}
                   onChange={(e) => setCompanyName(e.target.value)}
                   placeholder={cubicle === "legal"
                     ? "e.g. Alpha Legal LLP"
                     : "e.g. Shah & Co."}
                   className="bg-black/40 border-white/15 mt-1"
                   data-testid="office-vault-firm-input"/>
          </div>
        )}

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
              {contactType === "company" ? "Contact person" : "Full name *"}
            </label>
            <Input value={name}
                   onChange={(e) => setName(e.target.value)}
                   placeholder={contactType === "company" ? "Ravi Sinha" : "Adv. Anjali Singh"}
                   className="bg-black/40 border-white/15 mt-1"
                   data-testid="office-vault-name-input"/>
          </div>
          <div>
            <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
              Role / Designation
            </label>
            <Input value={roleLabel}
                   onChange={(e) => setRoleLabel(e.target.value)}
                   placeholder={cubicle === "legal" ? "Counsel · Contracts" : "CA · Audits"}
                   className="bg-black/40 border-white/15 mt-1"
                   data-testid="office-vault-role-input"/>
          </div>
          <div>
            <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
              Phone
            </label>
            <Input value={phone}
                   onChange={(e) => setPhone(e.target.value)}
                   placeholder="+91…"
                   className="bg-black/40 border-white/15 mt-1"
                   data-testid="office-vault-phone-input"/>
          </div>
          <div>
            <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
              Email
            </label>
            <Input value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   placeholder="ravi@alphalegal.in"
                   className="bg-black/40 border-white/15 mt-1"
                   data-testid="office-vault-email-input"/>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose} className="text-white/60">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}
                  className="rounded-full h-10 px-6 uppercase tracking-[0.22em] text-[11px]"
                  style={{ background: accent, color: "#0a0812" }}
                  data-testid="office-vault-invite-submit">
            <Send size={12}/> {busy ? "Adding…" : "Add"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function RetainModal({ row, cubicle, accent, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [notes,  setNotes]  = useState("");
  const [busy,   setBusy]   = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const paise = amount.trim() === ""
        ? null
        : Math.round(parseFloat(amount) * 100);
      if (paise !== null && (!Number.isFinite(paise) || paise < 0)) {
        toast.error("Enter a valid monthly retainer (₹)");
        setBusy(false); return;
      }
      await api.post(
        `/orgs/mine/office-cubicles/${cubicle}/invites/${row.id}/retain`,
        { monthly_retainer: paise, currency: "INR",
          retainer_notes: notes.trim() || undefined });
      toast.success(`${row.contact_type === "company" ? row.company_name : row.name} is on retainer`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="office-vault-retain-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-neutral-950 border border-white/15 rounded-2xl p-6 max-w-md w-full">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{color: accent}}>
          Retain · {cubicle}
        </div>
        <div className="font-display text-2xl mt-2 uppercase tracking-tight">
          {row.contact_type === "company" ? row.company_name : row.name}
        </div>
        <div className="mt-1 text-xs text-white/60">{row.role_label}</div>

        <div className="mt-5">
          <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
            Monthly retainer (₹ · optional)
          </label>
          <Input value={amount}
                 onChange={(e) => setAmount(e.target.value)}
                 placeholder="e.g. 25000"
                 type="number" min="0" step="1"
                 className="bg-black/40 border-white/15 mt-1"
                 data-testid="office-vault-retain-amount"/>
          <div className="mt-1 text-[10px] text-white/45">
            <Lock size={9} className="inline mr-1"/>
            Only owner, admin, and finance roles will see the amount.
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
            Notes (optional)
          </label>
          <Input value={notes}
                 onChange={(e) => setNotes(e.target.value)}
                 placeholder="Scope · SLA · payment terms"
                 className="bg-black/40 border-white/15 mt-1"
                 data-testid="office-vault-retain-notes"/>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose} className="text-white/60"
                  data-testid="office-vault-retain-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className="rounded-full h-10 px-6 uppercase tracking-[0.22em] text-[11px]"
                  style={{ background: accent, color: "#0a0812" }}
                  data-testid="office-vault-retain-submit">
            <Award size={12}/> {busy ? "Retaining…" : "Confirm retainer"}
          </Button>
        </div>
      </div>
    </div>
  );
}
